import React, { useEffect, useState } from 'react';
import { ProjectRow } from '../../../lib/types';
import { useAuth } from '../../hooks/useAuth';
import styles from './domain-ideas.module.css';

interface DomainOwnership {
  OwnerId: string;
  UserId: string;
  Domain: string;
  AssignedDate: string;
  AssignedByEmail: string;
}

interface IdeaWithStatus extends ProjectRow {
  ownedDomains?: string[];
}

export default function DomainIdeas() {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState<IdeaWithStatus[]>([]);
  const [ownedDomains, setOwnedDomains] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIdea, setSelectedIdea] = useState<IdeaWithStatus | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchDomainIdeas();
  }, []);

  const fetchDomainIdeas = async () => {
    try {
      setLoading(true);
      setError('');

      if (!user?.UserId) {
        throw new Error('Not authenticated');
      }

      // Get domains owned by current user
      const domainRes = await fetch(`/domain-ownership?userId=${encodeURIComponent(user.UserId)}`);
      const domainData = await domainRes.json();
      const domains = (domainData.data || []).map((d: DomainOwnership) => d.Domain);
      setOwnedDomains(domains);

      if (domains.length === 0) {
        setIdeas([]);
        setLoading(false);
        return;
      }

      // Get all projects and filter by owned domains
      const projectsRes = await fetch('/projects');
      const projectsData = await projectsRes.json();

      if (projectsData.success && projectsData.data) {
        const domainIdeas = projectsData.data.filter((p: ProjectRow) =>
          domains.includes(p.Domain) &&
          ['NewIdea', 'GatheringFeedback', 'ReadyForGovernance'].includes(p.Status)
        );
        setIdeas(domainIdeas);
      }
    } catch (err: any) {
      console.error('Failed to fetch domain ideas:', err);
      setError(err.message || 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (data.success) {
        // Refresh the list
        await fetchDomainIdeas();
        setSelectedIdea(null);
      } else {
        setError(data.error || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading your domain ideas...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Domain Ideas</h1>
        <p className={styles.subtitle}>
          Manage ideas in your domain{ownedDomains.length > 0 ? `: ${ownedDomains.join(', ')}` : ''}
        </p>
      </div>

      {error && (
        <div className={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {ownedDomains.length === 0 ? (
        <div className={styles.emptyState}>
          <p>You are not currently assigned to any domain.</p>
          <p>Contact your administrator to assign you to a domain.</p>
        </div>
      ) : ideas.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No ideas in your domains yet.</p>
          <p>Ideas will appear here once they're submitted.</p>
        </div>
      ) : (
        <div className={styles.ideasList}>
          {ideas.map(idea => (
            <div
              key={idea.ProjectId}
              className={`${styles.ideaCard} ${selectedIdea?.ProjectId === idea.ProjectId ? styles.selected : ''}`}
              onClick={() => setSelectedIdea(idea)}
            >
              <div className={styles.ideaHeader}>
                <div>
                  <h3 className={styles.ideaTitle}>{idea.Title}</h3>
                  <span className={styles.projectId}>{idea.ProjectId}</span>
                </div>
                <span className={`${styles.statusBadge} ${styles[idea.Status]}`}>
                  {idea.Status === 'NewIdea' ? 'New' : idea.Status === 'GatheringFeedback' ? 'Reviewing' : 'Ready'}
                </span>
              </div>
              <p className={styles.description}>{idea.Description.substring(0, 150)}...</p>
              <div className={styles.meta}>
                <span className={styles.domain}>{idea.Domain}</span>
                <span className={styles.date}>
                  {new Date(idea.CreatedDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedIdea && (
        <div className={styles.detailPanel}>
          <button
            className={styles.closeButton}
            onClick={() => setSelectedIdea(null)}
            disabled={actionLoading}
          >
            ×
          </button>

          <div className={styles.detailContent}>
            <h2>{selectedIdea.Title}</h2>
            <p className={styles.projectId}>{selectedIdea.ProjectId}</p>

            <div className={styles.section}>
              <h3>Description</h3>
              <p>{selectedIdea.Description}</p>
            </div>

            <div className={styles.section}>
              <h3>Details</h3>
              <table className={styles.detailsTable}>
                <tbody>
                  <tr>
                    <td>Domain:</td>
                    <td>{selectedIdea.Domain}</td>
                  </tr>
                  <tr>
                    <td>Work Type:</td>
                    <td>{selectedIdea.WorkType}</td>
                  </tr>
                  <tr>
                    <td>Status:</td>
                    <td>{selectedIdea.Status}</td>
                  </tr>
                  <tr>
                    <td>Submitted By:</td>
                    <td>{selectedIdea.CreatedByName}</td>
                  </tr>
                  <tr>
                    <td>Date:</td>
                    <td>{new Date(selectedIdea.CreatedDate).toLocaleDateString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.actions}>
              {selectedIdea.Status === 'NewIdea' && (
                <>
                  <button
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    onClick={() => handleStatusChange(selectedIdea.ProjectId, 'GatheringFeedback')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Updating...' : 'Begin Review'}
                  </button>
                  <button
                    className={`${styles.button} ${styles.buttonSecondary}`}
                    onClick={() => handleStatusChange(selectedIdea.ProjectId, 'ReadyForGovernance')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Updating...' : 'Fast-track to Governance'}
                  </button>
                </>
              )}

              {selectedIdea.Status === 'GatheringFeedback' && (
                <>
                  <button
                    className={`${styles.button} ${styles.buttonPrimary}`}
                    onClick={() => handleStatusChange(selectedIdea.ProjectId, 'ReadyForGovernance')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Updating...' : 'Move to Governance'}
                  </button>
                  <button
                    className={`${styles.button} ${styles.buttonSecondary}`}
                    onClick={() => setSelectedIdea(null)}
                    disabled={actionLoading}
                  >
                    Close
                  </button>
                </>
              )}

              {selectedIdea.Status === 'ReadyForGovernance' && (
                <button
                  className={`${styles.button} ${styles.buttonSecondary}`}
                  onClick={() => setSelectedIdea(null)}
                  disabled={actionLoading}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
